'use client';

import { Badge } from '@/components/ui/badge';

interface GradeRevealProps {
  grade: string;
  totalScore: number;
  projectName: string;
}

function getGradeInfo(grade: string): { cssVar: string; label: string } {
  switch (grade) {
    case 'A':
      return { cssVar: 'var(--grade-a)', label: 'Excellent' };
    case 'B':
      return { cssVar: 'var(--grade-b)', label: 'Good' };
    case 'C':
      return { cssVar: 'var(--grade-c)', label: 'Needs Improvement' };
    case 'D':
      return { cssVar: 'var(--grade-d)', label: 'Poor' };
    case 'F':
      return { cssVar: 'var(--grade-f)', label: 'Failing' };
    default:
      return { cssVar: 'var(--muted-foreground)', label: 'Unknown' };
  }
}

function getBarColor(score: number): string {
  if (score >= 90) return 'var(--grade-a)';
  if (score >= 80) return 'var(--grade-b)';
  if (score >= 70) return 'var(--grade-c)';
  if (score >= 60) return 'var(--grade-d)';
  return 'var(--grade-f)';
}

export function GradeReveal({ grade, totalScore, projectName }: GradeRevealProps) {
  const { cssVar, label } = getGradeInfo(grade);

  return (
    <div className="flex flex-col items-center gap-4 py-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Project name */}
      <div className="text-center">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          DCMA Quality Score
        </p>
        <h2 className="mt-1 text-base font-semibold text-foreground text-balance">{projectName}</h2>
      </div>

      {/* Compact grade circle */}
      <div
        className="flex h-28 w-28 flex-col items-center justify-center rounded-full ring-[3px]"
        style={
          {
            backgroundColor: `oklch(from ${cssVar} l c h / 0.1)`,
            '--tw-ring-color': `oklch(from ${cssVar} l c h / 0.3)`,
          } as React.CSSProperties
        }
      >
        <span
          className="text-4xl font-bold"
          style={{ color: cssVar }}
        >
          {grade}
        </span>
        <span className="text-xs text-muted-foreground font-medium">{totalScore}/100</span>
      </div>

      {/* Label */}
      <Badge
        variant="outline"
        className="px-3 py-0.5 text-xs font-semibold"
        style={{ color: cssVar, borderColor: cssVar }}
      >
        {label}
      </Badge>

      {/* Score bar */}
      <div className="w-full max-w-xs space-y-1">
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${Math.max(totalScore, 0)}%`,
              backgroundColor: getBarColor(totalScore),
            }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground/60">
          <span>0</span>
          <span>100</span>
        </div>
      </div>
    </div>
  );
}
